import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AudienceItem } from '../audience';
import { AudienceDetailsTabComponent } from './tabs/audience-details-tab.component';
import { AudienceVolumesTabComponent } from './tabs/audience-volumes-tab.component';
import { AudienceSociodemoTabComponent } from './tabs/audience-sociodemo-tab.component';
import { AudienceTagsTabComponent } from './tabs/audience-tags-tab.component';
import { AudienceChangelogTabComponent } from './tabs/audience-changelog-tab.component';

export type AdditionalInfoTab = 'details' | 'volumes' | 'sociodemo' | 'tags' | 'changelog';

/**
 * Advanced "Additional Info" card (Reach › Audiences detail, gated admin_dashboard.all in the parent).
 * Collapsible like the global extractions card; hosts the migrated insight tabs — Details, Volumes,
 * Sociodemo, Tags, Changelog. Tabs are lazy: a tab is only created the first time it's opened, then
 * kept (hidden) so switching back doesn't refetch. Selecting a different audience unmounts the visited
 * tabs (except the active one), so each reloads with fresh data when reopened.
 */
@Component({
  selector: 'app-audience-additional-info',
  standalone: true,
  imports: [
    CommonModule, TranslateModule,
    AudienceDetailsTabComponent, AudienceVolumesTabComponent,
    AudienceSociodemoTabComponent, AudienceTagsTabComponent, AudienceChangelogTabComponent
  ],
  templateUrl: './audience-additional-info.component.html',
  styleUrls: ['./audience-additional-info.component.scss']
})
export class AudienceAdditionalInfoComponent implements OnChanges {
  @Input() audience: AudienceItem | null = null;
  @Input() ownerLabel: string | null = null;

  readonly tabs: AdditionalInfoTab[] = ['details', 'volumes', 'sociodemo', 'tags', 'changelog'];
  activeTab: AdditionalInfoTab = 'details';
  /** Tabs that have been opened at least once (kept in the DOM, hidden when inactive). */
  visited = new Set<AdditionalInfoTab>();

  collapsed = sessionStorage.getItem('audiences.additionalInfo.collapsed') !== '0';

  private lastAudienceId: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['audience']) {
      const id = this.audience?.id ?? null;
      if (id !== this.lastAudienceId) {
        this.lastAudienceId = id;
        // New audience: drop every tab except the active one, so hidden tabs reload lazily on reopen.
        this.visited = new Set(this.collapsed ? [] : [this.activeTab]);
      }
    }
  }

  get audienceId(): string | null {
    return this.audience?.id ?? null;
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
    sessionStorage.setItem('audiences.additionalInfo.collapsed', this.collapsed ? '1' : '0');
    if (!this.collapsed) {
      this.visited.add(this.activeTab);
    }
  }

  selectTab(tab: AdditionalInfoTab): void {
    this.activeTab = tab;
    this.visited.add(tab);
  }

  isVisited(tab: AdditionalInfoTab): boolean {
    return this.visited.has(tab);
  }
}
